/**
 * TicketsPage - List of advisor's bug reports
 */

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import { useBreadcrumbs } from "@/shared/components/layout/DashboardLayout";
import {
  Card,
  CardContent,
  Badge,
  Button,
} from "@/shared/components/ui";
import {
  Bug,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Filter,
} from "lucide-react";
import { cn, formatDateTime } from "@/shared/lib/utils";
import type { Ticket, TicketStatus } from "@/shared/types";

const STATUS_CONFIG: Record<
  TicketStatus,
  { label: string; variant: "default" | "info" | "success" | "warning" | "error"; icon: React.ReactNode }
> = {
  new: {
    label: "Nowe",
    variant: "info",
    icon: <AlertCircle className="w-3 h-3" />,
  },
  analyzing: {
    label: "Analizowane",
    variant: "warning",
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
  },
  open: {
    label: "Otwarte",
    variant: "info",
    icon: <Bug className="w-3 h-3" />,
  },
  in_progress: {
    label: "W realizacji",
    variant: "warning",
    icon: <Clock className="w-3 h-3" />,
  },
  resolved: {
    label: "Rozwiązane",
    variant: "success",
    icon: <CheckCircle className="w-3 h-3" />,
  },
  closed: {
    label: "Zamknięte",
    variant: "default",
    icon: <CheckCircle className="w-3 h-3" />,
  },
};

const FILTER_OPTIONS: { value: TicketStatus | "all"; label: string }[] = [
  { value: "all", label: "Wszystkie" },
  { value: "new", label: "Nowe" },
  { value: "analyzing", label: "Analizowane" },
  { value: "open", label: "Otwarte" },
  { value: "in_progress", label: "W realizacji" },
  { value: "resolved", label: "Rozwiązane" },
  { value: "closed", label: "Zamknięte" },
];

export function TicketsPage() {
  const { setItems: setBreadcrumbs } = useBreadcrumbs();
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "all">("all");
  const [page, setPage] = useState(1);
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const limit = 10;

  useEffect(() => {
    setBreadcrumbs([
      { label: "Panel", href: "/" },
      { label: "Moje zgłoszenia" },
    ]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["tickets", statusFilter, page],
    queryFn: () =>
      api.getTickets({
        status: statusFilter === "all" ? undefined : [statusFilter],
        page,
        limit,
      }),
  });

  const tickets = (data?.tickets || []) as Ticket[];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
        <p className="text-gray-600">Nie udało się pobrać zgłoszeń</p>
        <Button
          onClick={() => window.location.reload()}
          variant="secondary"
          className="mt-4"
        >
          Spróbuj ponownie
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Moje zgłoszenia</h1>
          <p className="text-gray-500 mt-1">
            Historia Twoich zgłoszeń problemów i błędów
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500 mr-2">Status:</span>
            <div className="flex flex-wrap gap-1">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setStatusFilter(option.value);
                    setPage(1);
                  }}
                  className={cn(
                    "px-3 py-1 text-sm rounded-full transition-colors",
                    statusFilter === option.value
                      ? "bg-primary-100 text-primary-700 font-medium"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bug className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {statusFilter === "all"
                ? "Nie masz jeszcze żadnych zgłoszeń"
                : `Brak zgłoszeń ze statusem "${FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label}"`}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Użyj przycisku w prawym dolnym rogu, aby zgłosić problem
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => {
            const statusConfig = STATUS_CONFIG[ticket.status];
            const isExpanded = expandedTicket === ticket.id;

            return (
              <Card
                key={ticket.id}
                className={cn(
                  "transition-all cursor-pointer hover:shadow-md",
                  isExpanded && "ring-2 ring-primary-200"
                )}
                onClick={() => setExpandedTicket(isExpanded ? null : ticket.id)}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Status & Date */}
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={statusConfig.variant} className="flex items-center gap-1">
                          {statusConfig.icon}
                          {statusConfig.label}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {formatDateTime(ticket.createdAt)}
                        </span>
                      </div>

                      {/* Description preview */}
                      <p
                        className={cn(
                          "text-sm text-gray-700",
                          !isExpanded && "line-clamp-2"
                        )}
                      >
                        {ticket.description}
                      </p>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">ID zgłoszenia:</span>
                              <p className="font-mono text-xs mt-0.5">{ticket.id}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Ostatnia aktualizacja:</span>
                              <p className="mt-0.5">{formatDateTime(ticket.updatedAt)}</p>
                            </div>
                            {ticket.resolvedAt && (
                              <div>
                                <span className="text-gray-500">Rozwiązane:</span>
                                <p className="mt-0.5">{formatDateTime(ticket.resolvedAt)}</p>
                              </div>
                            )}
                          </div>

                          {/* Context - handle both nested and flat formats */}
                          {(ticket.pageUrl || ticket.context?.pageUrl) && (
                            <div className="bg-gray-50 rounded-lg p-3 text-xs">
                              <p className="font-medium text-gray-600 mb-2">
                                Kontekst zgłoszenia:
                              </p>
                              <div className="space-y-1 text-gray-500">
                                <div className="flex gap-2">
                                  <span className="font-medium w-24">Strona:</span>
                                  <span className="break-all">{ticket.pageUrl || ticket.context?.pageUrl}</span>
                                </div>
                                {(ticket.browserInfo || ticket.context?.browserInfo) && (
                                  <div className="flex gap-2">
                                    <span className="font-medium w-24">Przeglądarka:</span>
                                    <span>{ticket.browserInfo || ticket.context?.browserInfo}</span>
                                  </div>
                                )}
                                {(ticket.screenResolution || ticket.context?.screenResolution) && (
                                  <div className="flex gap-2">
                                    <span className="font-medium w-24">Rozdzielczość:</span>
                                    <span>{ticket.screenResolution || ticket.context?.screenResolution}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Expand indicator */}
                    <div className="flex-shrink-0">
                      <ExternalLink
                        className={cn(
                          "w-4 h-4 text-gray-400 transition-transform",
                          isExpanded && "rotate-90"
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Pokazuję {(page - 1) * limit + 1}-{Math.min(page * limit, total)} z {total} zgłoszeń
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
              Poprzednia
            </Button>
            <span className="text-sm text-gray-600 px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Następna
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
